class CustomModal extends HTMLElement {
	static modals = new Map(); // modalId → modal element
	static activeModal = null;
	static activeModals = []; // Stack of open modals
	static listenersInitialized = false;
	static _observers = new WeakMap(); // modal → { observers: ResizeObserver[], els: Element[] }

	connectedCallback() {
		CustomModal.registerModal(this);
	}

	disconnectedCallback() {
		CustomModal.teardownObservers(this);
		CustomModal.modals.delete(this.getAttribute('modal-id'));
	}

	static registerModal(modal) {
		const modalId = modal.getAttribute('modal-id');
		if (modalId) CustomModal.modals.set(modalId, modal);

		requestAnimationFrame(() => CustomModal.updateLayoutVariables(modal));
		CustomModal.setupObservers(modal);
	}

	static updateLayoutVariables(modal) {
		const wasHidden = !modal.classList.contains('modal--active');
		if (wasHidden) modal.classList.add('modal--measuring');

		try {
			const variableEls = modal.querySelectorAll('[data-variable-controller][data-variable-dimension][data-variable-title]');
			variableEls.forEach(el => {
				CustomModal.parseVariableValue(modal, el);
			});
		} finally {
			if (wasHidden) modal.classList.remove('modal--measuring');
		}
	}

	static parseVariableValue(modal, el) {
		const title = el.getAttribute('data-variable-title');
		const unit = el.getAttribute('data-variable-unit') || 'px';
		const dim = (el.getAttribute('data-variable-dimension') || 'height').toLowerCase();

		const rect = el.getBoundingClientRect();
		const value = dim === 'width' ? rect.width : rect.height;

		modal.style.setProperty(`--${title}`, `${Math.round(value)}${unit}`);
	}

	static setupObservers(modal) {
		const els = Array.from(modal.querySelectorAll('[data-variable-controller][data-variable-dimension][data-variable-title]'));
		if (!els.length) return;

		const observers = els.map(el => {
			const ro = new ResizeObserver(() => CustomModal.parseVariableValue(modal, el));
			ro.observe(el);
			return ro;
		});

		CustomModal._observers.set(modal, { observers, els });

		els.forEach(el => CustomModal.parseVariableValue(modal, el));
	}

	static teardownObservers(modal) {
		const entry = CustomModal._observers.get(modal);
		if (!entry) return;
		entry.observers.forEach(ro => ro.disconnect());
		CustomModal._observers.delete(modal);
	}

	static initEventListeners() {
		if (CustomModal.listenersInitialized) return;
		CustomModal.listenersInitialized = true;

		const modalClickHandlers = new Map([
			['[data-action="open-custom-modal"]', CustomModal.handleOpen],
			['[data-action="close-custom-modal"]', CustomModal.handleClose],
			['.PageOverlay.is-visible', CustomModal.handleOverlayClick],
		]);

		document.addEventListener('click', (e) => {
			for (const [selector, handler] of modalClickHandlers) {
				const target = e.target.closest(selector);
				if (target) {
					handler(target, e);
					break;
				}
			}
		});

		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && CustomModal.activeModal) {
				CustomModal.setModalState(CustomModal.activeModal, false);
			}
		});

		document.addEventListener('close-modals', (e) => {
			const sectionId = e.detail?.sectionId;
			const modals = sectionId
				? Array.from(document.querySelectorAll(`custom-modal[modal-id="${sectionId}"]`))
				: Array.from(document.querySelectorAll('custom-modal'));

			modals.forEach((modal) => CustomModal.setModalState(modal, false));
			// if (e.detail?.message) throwErrorNotice(e.detail.message);
		});

		const debounce = (fn, delay = 150) => {
			let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
		};

		const onResize = debounce(() => {
			for (const modal of CustomModal.modals.values()) {
				CustomModal.updateLayoutVariables(modal);
			}
		}, 150);

		window.addEventListener('resize', onResize);
	}

	static handleOverlayClick(_overlay, e) {
		if (!CustomModal.activeModal) return; // let theme handle its own modals
		e.preventDefault();
		// e.stopImmediatePropagation(); // if double-close due to theme, enable this
		CustomModal.setModalState(CustomModal.activeModal, false);
	}

	static handleOpen(trigger) {
		const modalId = trigger?.dataset.modalId;
		if (!modalId) return;

		const modal = CustomModal.modals.get(modalId);
		if (!modal) return;

		const isOpen = modal.dataset.open === 'true';
		CustomModal.setModalState(modal, !isOpen);
	}

	static handleClose(trigger) {
		const modal = trigger.closest('custom-modal');
		if (!modal) return;

		CustomModal.setModalState(modal, false);
	}

    static emit(modal, type, extraDetail = {}) {
        const modalId = modal.getAttribute('modal-id');
        modal.dispatchEvent(new CustomEvent(`custom-modal:${type}`, {
            detail: {
                modalId,
                activeModal: CustomModal.activeModal || null,
                ...extraDetail,
            },
            bubbles: true,
        }));
    }


	static setModalState(modal, isOpen) {
		const modalId = modal.getAttribute('modal-id');
		const triggers = document.querySelectorAll(`[data-action="open-custom-modal"][data-modal-id="${modalId}"]`);

		modal.dataset.open = String(isOpen);
		modal.inert = !isOpen;
		modal.toggleAttribute('aria-modal', isOpen);
		modal.toggleAttribute('role', isOpen);

        if (isOpen) {
            modal.classList.add('modal--active');
            requestAnimationFrame(() => requestAnimationFrame(() => CustomModal.updateLayoutVariables(modal)));
            CustomModal.emit(modal, 'open');
        } else {
            CustomModal.emit(modal, 'closing');
            modal.classList.add('transition-out');
            setTimeout(() => {
                modal.classList.remove('modal--active');
                modal.classList.remove('transition-out');
                CustomModal.emit(modal, 'close');
            }, 350);
        }        

		triggers.forEach((trigger) => {
			trigger.setAttribute('aria-expanded', String(isOpen));
			trigger.toggleAttribute('data-modal-open', isOpen);
		});

		if (isOpen) {
			if (!CustomModal.activeModals.includes(modal)) {
				CustomModal.activeModals.push(modal);
			}
		} else {
			CustomModal.activeModals = CustomModal.activeModals.filter(m => m !== modal);
		}

		CustomModal.updateActiveModal();

	}

	static updateActiveModal() {
		CustomModal.activeModal = CustomModal.activeModals.at(-1) || null;
		document.querySelector('html').classList.toggle('no-scroll', CustomModal.activeModal !== null);
		document.querySelector('.PageOverlay')?.classList.toggle('is-visible', CustomModal.activeModal !== null);
	}
}

customElements.define('custom-modal', CustomModal);
CustomModal.initEventListeners();
